import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  type RouterHistory,
  Outlet,
  redirect,
} from "@tanstack/react-router";

import {
  ApiError,
  type CatalogFilter,
  type CatalogSort,
  type IssueFilter,
  type Severity,
  type Stage,
} from "@/lib/api";

import { meQuery, safeRedirect } from "@/lib/auth";
import { productQuery } from "@/lib/products";
import { AppLayout } from "@/routes/app-layout";
import { CatalogPage } from "@/routes/catalog";
import { ForgotPasswordPage } from "@/routes/forgot-password";
import { InvitationPage } from "@/routes/invitation";
import { LoginPage } from "@/routes/login";
import type { ProductSearch } from "@/routes/product";
import { ResetPasswordPage } from "@/routes/reset-password";

/*
 * Pages other than login and catalogue are loaded on first visit (#39: the
 * bundle was over 500 kB). The router loads them while it navigates (route
 * components itself, the others in their loader), so the page does not
 * suspend; React holds a suspended page back for about 300 ms (B10).
 */
const ExportsPage = lazyRouteComponent(() => import("@/routes/exports"), "ExportsPage");
const HomePage = lazyRouteComponent(() => import("@/routes/home"), "HomePage");
const ImportPage = lazyRouteComponent(() => import("@/routes/import"), "ImportPage");
const JobsPage = lazyRouteComponent(() => import("@/routes/jobs"), "JobsPage");
const MediaPage = lazyRouteComponent(() => import("@/routes/media"), "MediaPage");
const MembersPage = lazyRouteComponent(() => import("@/routes/members"), "MembersPage");
const ProductPage = lazyRouteComponent(() => import("@/routes/product"), "ProductPage");
const ReportPage = lazyRouteComponent(() => import("@/routes/report"), "ReportPage");
const RulesPage = lazyRouteComponent(() => import("@/routes/rules"), "RulesPage");

type Context = { queryClient: QueryClient };

const rootRoute = createRootRouteWithContext<Context>()({
  component: Outlet,
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <h1 className="text-xl font-semibold">Seite nicht gefunden</h1>
    </div>
  ),
});

const tokenSearch = (search: Record<string, unknown>) => ({
  token: typeof search.token === "string" ? search.token : "",
});

// Pages without login.
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/anmelden",
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  // Already logged in: straight on.
  beforeLoad: async ({ context, search }) => {
    if (await context.queryClient.ensureQueryData(meQuery)) {
      throw redirect({ to: safeRedirect(search.redirect) });
    }
  },
  component: function Login() {
    return <LoginPage redirect={loginRoute.useSearch().redirect} />;
  },
});
const forgotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/passwort-vergessen",
  component: ForgotPasswordPage,
});
const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/passwort-neu",
  validateSearch: tokenSearch,
  component: function Reset() {
    return <ResetPasswordPage token={resetRoute.useSearch().token} />;
  },
});
const invitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/einladung",
  validateSearch: tokenSearch,
  component: function Invitation() {
    return <InvitationPage token={invitationRoute.useSearch().token} />;
  },
});

// Pages behind the login (AUTH-01).
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ context, location }) => {
    if (!(await context.queryClient.ensureQueryData(meQuery))) {
      throw redirect({ to: "/anmelden", search: { redirect: location.href } });
    }
  },
  component: AppLayout,
});
const SORTS: CatalogSort[] = ["title", "contributors", "isbn13", "publishing_date", "updated_at"];
const text = (value: unknown) => (typeof value === "string" && value ? value : undefined);
const list = (value: unknown) =>
  Array.isArray(value) && value.length && value.every((v) => typeof v === "string")
    ? (value as string[])
    : undefined;

/** Search and filters of the catalogue live in the address: reload and back keep them. */
function catalogSearch(search: Record<string, unknown>): CatalogFilter {
  return {
    q: text(search.q),
    validation: list(search.validation),
    status: list(search.status),
    form: list(search.form),
    publisher: list(search.publisher),
    media: list(search.media),
    from: text(search.from),
    to: text(search.to),
    sort: SORTS.find((s) => s === search.sort),
    dir: search.dir === "desc" ? "desc" : undefined,
  };
}

const catalogRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  validateSearch: catalogSearch,
  component: function Catalog() {
    const navigate = catalogRoute.useNavigate();
    return (
      <CatalogPage
        filter={catalogRoute.useSearch()}
        onFilter={(next) => void navigate({ search: next, replace: true })}
      />
    );
  },
});
const statusRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/status",
  component: HomePage,
});
const membersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/mitglieder",
  component: MembersPage,
});

const importRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/import",
  component: ImportPage,
});

const jobsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/auftraege",
  component: JobsPage,
});

const exportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/export",
  component: ExportsPage,
});

const mediaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/medien",
  component: MediaPage,
});

const SEVERITIES: Severity[] = ["error", "warning", "info"];
const STAGES: Stage[] = ["xml", "xsd", "codelist", "normalize", "rule"];

/** The filter of the report, in the address like that of the catalogue. */
function reportSearch(search: Record<string, unknown>): IssueFilter {
  return {
    severity: SEVERITIES.find((s) => s === search.severity),
    stage: STAGES.find((s) => s === search.stage),
    rule: text(search.rule),
  };
}

const reportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/auftraege/$id",
  validateSearch: reportSearch,
  loader: () => ReportPage.preload?.(),
  component: function Report() {
    const navigate = reportRoute.useNavigate();
    const id = Number(reportRoute.useParams().id);
    return (
      <ReportPage
        key={id}
        id={id}
        filter={reportRoute.useSearch()}
        onFilter={(next) => void navigate({ search: next, replace: true })}
        onJob={(next) => void navigate({ params: { id: String(next) }, search: {} })}
      />
    );
  },
});

/** `?v=3` shows version 3, `?feld=Product/…` jumps to a field. */
function productSearch(search: Record<string, unknown>): ProductSearch {
  const v = Number(search.v);
  return {
    v: Number.isInteger(v) && v > 0 ? v : undefined,
    feld: text(search.feld),
  };
}

const productRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/titel/$id",
  validateSearch: productSearch,
  loader: ({ context, params }) =>
    Promise.all([
      ProductPage.preload?.(),
      context.queryClient.prefetchQuery(productQuery(Number(params.id))),
    ]),
  component: function Product() {
    const navigate = productRoute.useNavigate();
    const id = Number(productRoute.useParams().id);
    const search = productRoute.useSearch();
    return (
      <ProductPage
        key={id}
        id={id}
        search={search}
        // Another version is a step back; a jump is not.
        onSearch={(next: ProductSearch) =>
          void navigate({ search: next, replace: next.v === search.v })
        }
      />
    );
  },
});

const rulesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/regeln",
  component: RulesPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotRoute,
  resetRoute,
  invitationRoute,
  appRoute.addChildren([
    catalogRoute,
    importRoute,
    jobsRoute,
    reportRoute,
    exportsRoute,
    mediaRoute,
    productRoute,
    rulesRoute,
    membersRoute,
    statusRoute,
  ]),
]);

/** Router and query client; tests pass a memory history. */
export function createApp(history?: RouterHistory) {
  // A 401 on any request: the session has expired or the membership is
  // gone. Back to the login, then return to the same page.
  const onError = (error: Error) => {
    if (!(error instanceof ApiError && error.status === 401)) return;
    queryClient.setQueryData(meQuery.queryKey, null);
    const here = router.state.location;
    if (here.pathname !== "/anmelden") {
      void router.navigate({ to: "/anmelden", search: { redirect: here.href } });
    }
  };
  const queryClient = new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        staleTime: 5_000,
        // Client errors do not get better by asking again.
        retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 1,
      },
    },
  });
  const router = createRouter({ routeTree, context: { queryClient }, history });
  return { router, queryClient };
}

export const { router, queryClient } = createApp();

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
